import React, { useCallback, useEffect, useState } from 'react';
import PollerHealthService from './PollerHealthService';

// Refresh on its own so the tab can stay open on a second screen.
const AUTO_REFRESH_MS = 60 * 1000;

const STATUS = {
    ok: { label: 'ปกติ', badge: 'bg-emerald-100 text-emerald-800 border-emerald-300', card: 'border-emerald-300' },
    warning: { label: 'เตือน', badge: 'bg-amber-100 text-amber-800 border-amber-300', card: 'border-amber-300' },
    problem: { label: 'มีปัญหา', badge: 'bg-red-100 text-red-800 border-red-300', card: 'border-red-400' },
    starting: { label: 'กำลังเริ่ม', badge: 'bg-gray-100 text-gray-700 border-gray-300', card: 'border-gray-300' },
};
const STATUS_RANK = { problem: 3, warning: 2, starting: 1, ok: 0 };

// Tier keys are what each poller records; the labels are the schedule they mean.
const TIER_LABELS = {
    under1Day: 'อายุ < 1 วัน', under5Days: '1–5 วัน', under30Days: '5–30 วัน', older: '> 30 วัน',
    firstHour: 'อายุ < 1 ชม.', firstDay: '1 ชม. – 1 วัน', upTo30Days: '1–30 วัน',
};

const minutes = (ms) => Math.round(ms / 60000);

const timeAgo = (iso, now) => {
    if (!iso) return '—';
    const m = minutes(now - new Date(iso).getTime());
    if (m < 1) return 'เมื่อสักครู่';
    if (m < 60) return `${m} นาทีที่แล้ว`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} ชม. ${m % 60} นาทีที่แล้ว`;
    return `${Math.floor(h / 24)} วันที่แล้ว`;
};

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString('th-TH', { hour12: false }) : '—');

const Stat = ({ label, value, alert }) => (
    <div className={`rounded-md border px-3 py-2 ${alert && value > 0 ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'}`}>
        <div className="text-xs text-gray-500">{label}</div>
        <div className="text-lg font-semibold">{value ?? 0}</div>
    </div>
);

const PollerCard = ({ poller, now }) => {
    const status = STATUS[poller.status] || STATUS.starting;
    const tiers = poller.tierCounts ? Object.entries(poller.tierCounts) : [];
    const tierTotal = tiers.reduce((sum, [, n]) => sum + Number(n || 0), 0);

    return (
        <div className={`rounded-lg border-2 bg-white p-4 ${status.card}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="text-lg font-bold">{poller.label}</h2>
                <span className={`px-3 py-1 rounded-full border text-sm font-semibold ${status.badge}`}>{status.label}</span>
            </div>

            {poller.reasons.length > 0 && (
                <ul className="mt-2 list-disc list-inside text-sm text-gray-800">
                    {poller.reasons.map((r) => <li key={r}>{r}</li>)}
                </ul>
            )}

            <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <div className="text-gray-500">รอบล่าสุด (tick)</div>
                <div title={fmtTime(poller.lastTickAt)}>{timeAgo(poller.lastTickAt, now)}</div>
                <div className="text-gray-500">รอบล่าสุดที่มีงาน/ปัญหา</div>
                <div title={fmtTime(poller.lastRunAt)}>{timeAgo(poller.lastRunAt, now)}</div>
                {poller.backlog && (
                    <>
                        <div className="text-gray-500">ค้างเช็ค (เลยกำหนด)</div>
                        <div className={poller.backlog.overdue > 0 ? 'text-amber-700 font-semibold' : ''}>
                            {poller.backlog.overdue} รายการ
                            {poller.backlog.overdue > 0 && ` · รอนานสุด ${minutes(poller.backlog.oldestOverdueMs)} นาที`}
                        </div>
                    </>
                )}
            </div>

            <h3 className="mt-4 text-sm font-semibold text-gray-700">รายการที่ติดตาม แยกตามอายุ {tierTotal > 0 && `(รวม ${tierTotal})`}</h3>
            {tiers.length ? (
                <div className="mt-1 grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {tiers.map(([key, n]) => <Stat key={key} label={TIER_LABELS[key] || key} value={n} />)}
                </div>
            ) : (
                <p className="text-sm text-gray-400 mt-1">ยังไม่มีรอบที่บันทึกไว้</p>
            )}

            <h3 className="mt-4 text-sm font-semibold text-gray-700">24 ชั่วโมงล่าสุด</h3>
            <div className="mt-1 grid grid-cols-3 sm:grid-cols-6 gap-2">
                <Stat label="รอบที่บันทึก" value={poller.last24h.runs} />
                <Stat label="เช็คแล้ว" value={poller.last24h.checked} />
                <Stat label="เช็คไม่สำเร็จ" value={poller.last24h.failed} alert />
                <Stat label="โดนจำกัด (429)" value={poller.last24h.rateLimited} alert />
                <Stat label="โดนบล็อก" value={poller.last24h.blockedRuns} alert />
                <Stat label="รอบล้มเหลว" value={poller.last24h.crashedRuns} alert />
            </div>

            <h3 className="mt-4 text-sm font-semibold text-gray-700">ปัญหาล่าสุด</h3>
            {poller.recentProblems.length ? (
                <div className="mt-1 overflow-x-auto">
                    <table className="min-w-full text-xs">
                        <thead>
                            <tr className="text-left text-gray-500 border-b">
                                <th className="py-1 pr-3">เวลา</th>
                                <th className="py-1 pr-3">เช็ค</th>
                                <th className="py-1 pr-3">ไม่สำเร็จ</th>
                                <th className="py-1 pr-3">429</th>
                                <th className="py-1 pr-3">บล็อก</th>
                                <th className="py-1">ข้อผิดพลาด</th>
                            </tr>
                        </thead>
                        <tbody>
                            {poller.recentProblems.map((row) => (
                                <tr key={row.startedAt} className="border-b last:border-0 align-top">
                                    <td className="py-1 pr-3 whitespace-nowrap">{fmtTime(row.startedAt)}</td>
                                    <td className="py-1 pr-3">{row.checked}</td>
                                    <td className="py-1 pr-3">{row.failed}</td>
                                    <td className="py-1 pr-3">{row.rateLimited}</td>
                                    <td className="py-1 pr-3">{row.blocked ? 'ใช่' : ''}</td>
                                    <td className="py-1 font-mono break-all">{row.errorSample || ''}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p className="text-sm text-gray-400 mt-1">ไม่มี</p>
            )}
        </div>
    );
};

const PollerHealth = () => {
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [fetchedAt, setFetchedAt] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await PollerHealthService.getPollerHealth();
            setData(res.data);
            setError('');
            setFetchedAt(Date.now());
        } catch (err) {
            setError(err?.response?.data?.message || 'โหลดสถานะไม่สำเร็จ');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const timer = setInterval(load, AUTO_REFRESH_MS);
        return () => clearInterval(timer);
    }, [load]);

    const now = fetchedAt || Date.now();
    const worst = data
        ? data.pollers.reduce((acc, p) => (STATUS_RANK[p.status] > STATUS_RANK[acc] ? p.status : acc), 'ok')
        : null;
    const overall = worst ? STATUS[worst] : null;
    const t = data?.thresholds;

    return (
        <div className="p-4 max-w-5xl mx-auto">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                <h1 className="text-2xl font-bold">สถานะ Poller (Mercari / PayPay)</h1>
                <div className="flex items-center gap-3 text-sm">
                    {fetchedAt && <span className="text-gray-500">อัปเดต {fmtTime(fetchedAt)}</span>}
                    <button
                        onClick={load}
                        disabled={loading}
                        className="px-3 py-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                    >
                        {loading ? 'กำลังโหลด...' : 'รีเฟรช'}
                    </button>
                </div>
            </div>
            <p className="text-gray-600 mb-4 text-sm">
                ตัวเช็คราคาอัตโนมัติทำงานอยู่หรือไม่ และ Mercari / PayPay กำลังจำกัดหรือบล็อกเราหรือเปล่า รีเฟรชเองทุก 1 นาที
            </p>

            {error && <div className="mb-4 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>}

            {overall && (
                <div className={`mb-4 rounded-md border px-3 py-2 text-sm font-semibold ${overall.badge}`}>
                    ภาพรวม: {overall.label}
                </div>
            )}

            {data && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {data.pollers.map((p) => <PollerCard key={p.poller} poller={p} now={now} />)}
                </div>
            )}

            {t && (
                <p className="mt-4 text-xs text-gray-500">
                    เกณฑ์: <b>มีปัญหา</b> = ไม่มีรอบเกิน {minutes(t.staleTickMs)} นาที, โดนบล็อก หรือรอบล้มเหลว ใน {minutes(t.problemWindowMs) / 60} ชม. ล่าสุด ·{' '}
                    <b>เตือน</b> = โดน 429 หรือเช็คไม่สำเร็จ ใน {minutes(t.warningWindowMs) / 60} ชม. ล่าสุด ·{' '}
                    รอบที่ไม่มีงานและไม่มีปัญหาจะไม่ถูกบันทึก แต่ยังนับว่าทำงานอยู่
                </p>
            )}
        </div>
    );
};

export default PollerHealth;
